import { pubsub } from '@services';
import { notyf } from '@modules';
import { debugError, debugLog } from '@utils';

class PicturatorUploadWidget extends HTMLElement {
  static debug = false;

  /**
   * The format/material this button hands to the configurator, read from the
   * attribute ON ACCESS — never cached.
   *
   * It used to be parsed once in the constructor, which meant the PDP's
   * `syncPreselect` had to write twice: the `data-preselect` attribute AND a
   * mutation of this object (`Object.assign(el.preselectData, …)`), with a
   * comment explaining why. Two writes of one fact drift, and the drift is
   * invisible to the obvious check — reading the attribute shows the right
   * value while the redirect uses a stale cache. That is exactly how the
   * NGE-10843 hand-off was verified.
   *
   * Reading on access also makes DELETION expressible: `Object.assign` can
   * only add or overwrite, so a format that no longer applies to the chosen
   * material could not be cleared (NGE-12308). One source of truth, and the
   * attribute is it.
   */
  get preselectData() {
    try {
      return JSON.parse(this.dataset.preselect || '{}');
    } catch (_) {
      return {};
    }
  }

  constructor() {
    super();
    this.id = this.dataset.id;
    this.picturatorUrl = this.dataset.picturatorUrl || '/';
    this.galleryUrl = this.dataset.galleryUrl || '/pages/gallery';
    this.redirectUrl = this.dataset.redirectUrl || '';
    if (!this.id) {
      throw new Error('A unique ID is required for upload button');
    }

    // Config
    this.config = {
      containerId: `${this.id}`,
      shop: window.theme.shop || 'STIT',
      env: window.theme.env || 'production',
      campaign: window.theme.campaign.handle,
      channel: window.theme.channel.handle || 'seo',
      tier: parseInt(window.theme.tier || 0),
      priceTier: parseInt(window.theme.tier || 0),
      buttonText: 'Upload',
      maxFileCount: 25,
      allowMultiple: true,
    };
    try {
      this.config = { ...this.config, ...JSON.parse(this.dataset.config || '{}') };
    } catch (error) {
      debugError('Failed to parse configurator liquid config:', error);
      this.config = { ...this.config };
    }
  }

  connectedCallback() {
    const config = {
      ...this.config,
    };

    // Initialize the configurator
    debugLog('Picturator: Initialize upload widget', config);
    this.uploadWidget = window.UploadWidget.init(config);

    // Event listeners
    this.uploadWidget.on('upload_ready', this.#onUploadReady.bind(this));
    this.uploadWidget.on('upload_success', this.#onUploadSuccess.bind(this));
    this.uploadWidget.on('upload_error', this.#onUploadError.bind(this));
  }

  disconnectedCallback() {
    if (!this.uploadWidget) return;
    this.uploadWidget.off('upload_ready', this.#onUploadReady.bind(this));
    this.uploadWidget.off('upload_success', this.#onUploadSuccess.bind(this));
    this.uploadWidget.off('upload_error', this.#onUploadError.bind(this));

    // Destroy the upload widget
    this.uploadWidget.destroy();

    // Reset the upload widget reference
    this.uploadWidget = null;
  }

  /**
   * Handle upload ready event
   */
  #onUploadReady() {
    PicturatorUploadWidget.debug && debugLog('Picturator: Upload widget is ready!');
  }

  /**
   * Handle upload success event
   * @param {*} result
   */
  #onUploadSuccess(result) {
    PicturatorUploadWidget.debug && debugLog('Picturator: Upload widget is successful!', result);
    const keys = Array.isArray(result?.files)
      ? result.files.map((f) => f?.uploadKey).filter(Boolean)
      : [];
    const uploadKey = keys[0];
    if (!uploadKey) {
      debugError('Picturator: Upload key not found');
      return;
    }
    this.handleUpload(keys);
  }

  /**
   * Handle upload error event
   * @param {*} error
   */
  #onUploadError(error) {
    PicturatorUploadWidget.debug && debugLog('Picturator: Upload widget is error!', error);

    // NGE-11887: a render crash the widget RECOVERED from still emits upload_error
    // so loaders hide, but the upload succeeded and upload_success follows — telling
    // the customer something failed would be false. `recovered` comes from the crash
    // boundary itself (the only thing that knows whether a remount follows); the
    // TERMINAL crash arrives with recovered: false and must keep its toast — the
    // widget is a dead end at that point and silence would strand the customer with
    // no cue to reload (davis, #201 review). Old bundles that emit isRenderCrash
    // without the flag also fall through to the toast — the safe direction.
    if (error?.isRenderCrash && error?.recovered) return;

    // Normalize error message
    const rawMessage = typeof error === 'string' ? error : error?.message || '';
    const lowerMessage = rawMessage.toLowerCase();

    // Try to map to localized toast translations
    let localizedMessage = rawMessage;

    // DPI / resolution-specific error
    if (lowerMessage.includes('dpi') || lowerMessage.includes('resolution')) {
      localizedMessage =
        window.toast_translations?.upload?.low_dpi ||
        window.toast_translations?.upload?.generic_error ||
        rawMessage;
    } else {
      localizedMessage = window.toast_translations?.upload?.generic_error || rawMessage;
    }

    // Show localized error notification
    if (localizedMessage) {
      notyf.error(localizedMessage);
    }
  }

  /**
   * Handle the upload of the image
   * @param {*} uploadKey
   */
  handleUpload(uploadKeys) {
    const keys = Array.isArray(uploadKeys)
      ? uploadKeys.filter(Boolean)
      : [uploadKeys].filter(Boolean);
    const firstKey = keys[0];
    if (!firstKey) {
      debugError('Picturator: Upload key not found');
      return;
    }

    const preselectParams = Object.entries(this.preselectData)
      .map(([key, val]) => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`)
      .join('&');

    // If redirectUrl is provided (e.g. Photo Prints), always go there (not gallery/configurator).
    if (this.redirectUrl) {
      const joiner = this.redirectUrl.includes('?') ? '&' : '?';
      const keyParams = keys.length > 1 ? `uploadKeys=${keys.join(',')}` : `uploadKey=${firstKey}`;
      const url = `${this.redirectUrl}${joiner}${keyParams}${preselectParams ? `&${preselectParams}` : ''}`;
      window.location.href = url;
      return;
    }

    // Multiple images uploaded → redirect to gallery page so user can select which to configure
    if (keys.length > 1 && this.galleryUrl) {
      // Store preselect context in sessionStorage for the gallery page to use
      try {
        sessionStorage.setItem(
          'picturator_gallery_context',
          JSON.stringify({
            preselect: this.preselectData,
            picturatorUrl: this.picturatorUrl,
          })
        );
      } catch (e) {}
      // Pass uploadKeys through the URL so the gallery has an authoritative
      // list for this upload session, independent of IndexedDB availability.
      const joiner = this.galleryUrl.includes('?') ? '&' : '?';
      window.location.href = `${this.galleryUrl}${joiner}uploadKeys=${keys.join(',')}`;
      return;
    }

    // Single image uploaded → redirect to configurator
    if (window.location.pathname !== this.picturatorUrl) {
      const preselectParams = Object.entries(this.preselectData)
        .map(([key, val]) => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`)
        .join('&');
      const url = `${this.picturatorUrl}?uploadKey=${firstKey}&${preselectParams}`;
      window.location.href = url;
    } else {
      pubsub.publish(`upload:image`, {
        key: keys.length ? keys : [firstKey],
        preselect: this.preselectData,
      });
    }
  }
}

customElements.define('picturator-upload-widget', PicturatorUploadWidget);
